'use client';

import { useEffect, useRef, type KeyboardEvent } from 'react';
import { cn } from '@lib/utils';
import { TrackedAnchor } from '@/components/ancher';
import {
  TranslatedNavItem,
  TranslatedNavTree,
  useSiteNav,
} from '@/hooks/useMenu.hook';
import { HOVER_CLOSE_DELAY_MS, useMegaMenu } from './use-mega-menu';
import { focusTriggerByKey, wrapIndex } from './menubar-keys';

interface MegaMenuContentProps {
  item: TranslatedNavItem;
}

// 메가메뉴 height 가 메뉴마다 다르면 hover 전환 시 GNB 가 점프 — Products(가장 큼)
// 기준으로 고정. backdrop blur 영역도 같이 안정화 (RCA: isolation:isolate 와 같은 결).
const MEGA_MENU_MIN_HEIGHT = 'min-h-[318px]';

const MegaMenuContent = ({ item }: MegaMenuContentProps) => (
  <div
    className={cn(
      'mx-auto flex max-w-[1320px] gap-12 px-0 pb-12 pt-2 2xl:container',
      MEGA_MENU_MIN_HEIGHT,
    )}
  >
    <div className="w-[200px] shrink-0">
      <h3 className="text-[24px] font-semibold leading-[32px] tracking-[-0.464px] text-[#040435]">
        {item.label}
      </h3>
      {item.description && (
        <p className="mt-3 text-[14px] font-medium leading-[22px] tracking-[-0.128px] text-[#6C7380]">
          {item.description}
        </p>
      )}
    </div>
    <div className="flex flex-1 gap-12">
      {item.groups?.map((group, groupIndex) => (
        <div
          key={group.title ?? groupIndex}
          role="none"
          className="min-w-[140px]"
        >
          {group.title && (
            <p className="mb-4 text-[14px] font-medium leading-[22px] tracking-[-0.128px] text-[#6C7380]">
              {group.title}
            </p>
          )}
          <ul role="none" className="flex flex-col gap-4">
            {group.items.map(link => (
              <li key={link.href} role="none">
                <TrackedAnchor
                  role="menuitem"
                  label={link.gtagLabel}
                  position="nav"
                  href={link.href}
                  target={link.target}
                  rel={link.rel}
                  className="text-[16px] font-semibold leading-[24px] tracking-[-0.18px] text-[#040435] outline-none transition-colors hover:text-[#ff142d] focus-visible:text-[#ff142d] focus-visible:underline focus-visible:underline-offset-4"
                >
                  {link.label}
                </TrackedAnchor>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  </div>
);

interface MegaMenuBackdropProps {
  isVisible: boolean;
}

// backdrop 은 시각 효과 전용 — pointer-events 항상 none 으로 두어 페이지 콘텐츠 클릭이
// 가로채이지 않도록 한다. 메뉴 닫힘은 document level pointerdown listener 가 처리.
const MegaMenuBackdrop = ({ isVisible }: MegaMenuBackdropProps) => (
  <div
    aria-hidden
    className={cn(
      'pointer-events-none fixed inset-0 top-0 -z-10 bg-white/30 backdrop-blur-[25px] transition-opacity duration-300 ease-out motion-reduce:transition-none',
      isVisible ? 'opacity-100' : 'opacity-0',
    )}
  />
);

// WAI-ARIA submenu 키보드 (Esc/Arrow/Tab 한정).
// - Esc, Tab boundary, ←/→: 닫고 trigger 복귀 또는 이동
// - ↓/↑: panel 내 wrap
// helper 가 panel ref, 전체 menubar items, 활성 key, close 액션을 인자로 받아 closure 의존 최소화.
const handleSubmenuKeyDown = (
  e: KeyboardEvent<HTMLDivElement>,
  ctx: {
    panel: HTMLElement | null;
    items: TranslatedNavTree;
    activeMenuKey: string | undefined;
    closeMenu: () => void;
  },
) => {
  const { panel, items, activeMenuKey, closeMenu } = ctx;

  if (e.key === 'Escape') {
    e.preventDefault();
    closeMenu();
    focusTriggerByKey(activeMenuKey);
    return;
  }

  const anchors = Array.from(
    panel?.querySelectorAll<HTMLAnchorElement>('a') ?? [],
  );
  if (anchors.length === 0) return;
  const idx = anchors.indexOf(document.activeElement as HTMLAnchorElement);

  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
    if (idx === -1) return;
    e.preventDefault();
    const offset = e.key === 'ArrowDown' ? 1 : -1;
    anchors[wrapIndex(idx, offset, anchors.length)].focus();
    return;
  }

  if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
    e.preventDefault();
    const current = items.findIndex(item => item.key === activeMenuKey);
    if (current === -1) return;
    const offset = e.key === 'ArrowLeft' ? -1 : 1;
    closeMenu();
    focusTriggerByKey(items[wrapIndex(current, offset, items.length)].key);
    return;
  }

  if (e.key !== 'Tab' || idx === -1) return;
  const onBoundary =
    (e.shiftKey && idx === 0) || (!e.shiftKey && idx === anchors.length - 1);
  if (!onBoundary) return;
  e.preventDefault();
  closeMenu();
  focusTriggerByKey(activeMenuKey);
};

/**
 * MegaMenu — InnerWrapper 의 둘째 row 로 layout flow 에 참여.
 * grid-rows 0fr → 1fr 트랜지션으로 row height 자라남 = InnerWrapper 전체 height 도 자라남
 * → "GNB 가 늘어나는" 모션. 활성 1개 콘텐츠만 렌더.
 */
export const MegaMenu = () => {
  const { createMenu } = useSiteNav();
  const { activeMenuKey, registerMenuRoot, cancelDelayedClose, closeMenu } =
    useMegaMenu();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const items = createMenu();
  const itemsWithMegaMenu = items.filter(item => !!item.groups?.length);
  const activeItem = itemsWithMegaMenu.find(item => item.key === activeMenuKey);
  const isOpen = !!activeItem;

  // 닫힘 트랜지션 동안 마지막 콘텐츠 유지 + locale 토글 시 즉시 새 번역 반영을 모두 만족시키려면
  // 렌더 시점에 동기적으로 갱신해야 한다 (useEffect 는 commit 후라 첫 open 한 frame 빈 panel +
  // locale 만 바뀌면 activeMenuKey 동일 → effect 미실행 → stale 콘텐츠 문제). ref 의 mutation
  // 이 idempotent 라 render 중 갱신해도 안전.
  const lastRenderedRef = useRef<TranslatedNavItem | undefined>(activeItem);
  if (activeItem) lastRenderedRef.current = activeItem;
  const renderedItem = activeItem ?? lastRenderedRef.current;

  useEffect(
    function registerRootForOutsideClick() {
      registerMenuRoot(rootRef.current);
      return function unregisterRoot() {
        registerMenuRoot(null);
      };
    },
    [registerMenuRoot],
  );

  return (
    <>
      <MegaMenuBackdrop isVisible={isOpen} />
      <div
        ref={rootRef}
        role="menu"
        // 헤더 높이를 재는 쪽(ScrollStage)이 펼침 높이를 빼는 데 쓴다
        data-mega-menu
        // label 은 ReactNode(<br> 포함 가능) — aria-label 은 평문 라벨일 때만 사용.
        aria-label={
          isOpen && typeof renderedItem?.label === 'string'
            ? renderedItem.label
            : 'Mega menu'
        }
        aria-hidden={!isOpen}
        // 닫힘 시 inert 로 키보드 focus 차단 — aria-hidden 만으로는 Tab 진입 못 막음.
        inert={!isOpen}
        // nav 와 인접 — nav→패널 이동 시 nav.onMouseLeave(닫힘 예약)를 이 onMouseEnter 가 취소해 유지.
        onMouseEnter={cancelDelayedClose}
        onMouseLeave={() => closeMenu({ delay: HOVER_CLOSE_DELAY_MS })}
        onFocus={cancelDelayedClose}
        onBlur={() => closeMenu({ delay: HOVER_CLOSE_DELAY_MS })}
        onKeyDown={e =>
          handleSubmenuKeyDown(e, {
            panel: rootRef.current,
            items,
            activeMenuKey,
            closeMenu,
          })
        }
        className={cn(
          'grid transition-all motion-reduce:transition-none',
          isOpen
            ? 'grid-rows-[1fr] duration-[150ms] ease-out'
            : 'grid-rows-[0fr] duration-[240ms] ease-in',
        )}
      >
        <div className="min-h-0 overflow-hidden">
          {renderedItem && <MegaMenuContent item={renderedItem} />}
        </div>
      </div>
    </>
  );
};
